import { observable, toJS } from 'mobx';
import { Flowstep, Operation, ProviderType } from 'shared/types/flowstep';
import { ErrorReaction } from 'shared/utils/flowstep';
import { CellAddressPickerValue } from '@v2/components/base/CellAddressPicker/types';
import { ValidationErrorsMap } from '@v2/states/validation';
import { makeAutoObservableExt } from '@v2/utils/mobx/make-auto-observable-ext';
import { dataStore, flowstepStorage } from 'src/stores';
import { runCancelableAction, runShortAction } from 'src/services/utils';
import { CancelToken } from 'shared/utils/cancelToken';
import { executeFlowsteps } from 'src/services/execution';
import office from 'src/services/office';

type BaseValidationErrorKey = 'name' | 'errorReaction' | 'timestampCell' | string;

export interface FlowstepMeta {
  operation: Operation;
  provider: ProviderType;
}

export abstract class BaseEditorState {
  initialFlowstep?: Flowstep;

  name: string = '';
  errorReaction: string = ErrorReaction.AskUser;
  protectionPassword: string | undefined = undefined;
  timestampCell: CellAddressPickerValue | null = null;
  recalculateFormulas: boolean = true;
  flowstep: Flowstep | undefined = undefined;

  protected constructor(public flowstepMeta: FlowstepMeta) {
    makeAutoObservableExt(this, BaseEditorState, {
      initialFlowstep: observable.ref,
    });
  }

  async findAndLoadSimilarFlowstep(
    loadTheSameTypeAsExact: boolean = false,
    target?: { worksheetId: string; targetCell: string },
  ) {
    await runShortAction(async () => {
      const flowstepTarget = await office.getFlowstepTarget(target);
      const { worksheetId, targetCell } = flowstepTarget;
      const { operation, provider } = this.flowstepMeta;
      const allWithSameTarget: Flowstep[] = dataStore.flowSteps.filter(
        (x) =>
          //filter new flowsteps
          !!x.provider && x.options.worksheetId === worksheetId && x.options.target === targetCell,
      );
      const allSimilarTypesWithSameTarget = allWithSameTarget.filter(this.isSimilarFlowstep);
      const theSameType = allSimilarTypesWithSameTarget.find(
        (x) => x.provider === provider && x.operation === operation,
      );
      if (allSimilarTypesWithSameTarget.length > 0) {
        const isExact = loadTheSameTypeAsExact && !!theSameType;
        const flowstep = structuredClone(toJS(theSameType ?? allSimilarTypesWithSameTarget[0]));
        if (!isExact) {
          flowstep.name = '';
          flowstep.operation = operation;
        }
        this.onFlowstepLoaded(flowstep, { isExact, flowstepTarget });
        return;
      }

      this.tryToLoadDifferentTypeFlowstep?.(allWithSameTarget, flowstepTarget);
    });
  }

  tryToLoadDifferentTypeFlowstep?(sameTargetFlowsteps: Flowstep[], target: any): void {}
  abstract isSimilarFlowstep(flowstep: Flowstep): boolean;

  onFlowstepLoaded(flowstep: Flowstep, meta: { isExact?: boolean; flowstepTarget?: any }): void {
    this.initialFlowstep = flowstep;

    if (meta.isExact) {
      this.flowstep = flowstep;
    }
    this.name = flowstep.name;
    this.errorReaction = flowstep.errorReaction || ErrorReaction.AskUser;

    const options = flowstep.options || {};
    this.protectionPassword = options.protectionPassword;
    this.timestampCell = options.timestampCell || null;
    this.recalculateFormulas = options.recalculateFormulas ?? false;
  }

  buildFlowstep(): Partial<Flowstep['options']> {
    return {
      options: {
        protectionPassword: this.protectionPassword,
        timestampCell: toJS(this.timestampCell),
        recalculateFormulas: this.recalculateFormulas,
      },
    };
  }

  async loadFlowstep(id?: string, createNew?: boolean) {
    if (id) {
      const loadedFlowstep = flowstepStorage.getFlowstep(id);
      if (loadedFlowstep) {
        this.onFlowstepLoaded(loadedFlowstep, { isExact: true });
      }
    } else {
      await this.findAndLoadSimilarFlowstep(!createNew);
    }
  }

  async saveFlowstep(options: { makeCopy?: boolean; targetFlowId?: string } = {}) {
    const { makeCopy = false, targetFlowId } = options;
    const flowstep = this.buildFlowstep();

    const updatedFlowstep = await flowstepStorage.saveFlowstep(flowstep as Flowstep, {
      flowId: targetFlowId || dataStore.selectedFlowId,
      makeCopy,
    });

    this.flowstep = updatedFlowstep;
    this.initialFlowstep = updatedFlowstep;
  }

  async runFlowstep() {
    const flowstepToRun = this.buildFlowstep();
    await runCancelableAction(async (cancelToken: CancelToken) => {
      await executeFlowsteps([{ ...flowstepToRun, originalFlowstep: flowstepToRun! }], cancelToken);
    });
  }

  async copyFlowstepToFlow(targetFlowId: string) {
    const flowstep = this.buildFlowstep() as Flowstep;
    const copiedFlowstep = await flowstepStorage.copyFlowstepToFlow(flowstep, targetFlowId);
    this.flowstep = copiedFlowstep;
    this.initialFlowstep = copiedFlowstep;
  }

  async moveFlowstepToFlow(targetFlowId: string) {
    const flowstep = this.buildFlowstep() as Flowstep;
    const updatedFlowstep = await flowstepStorage.moveFlowstepToFlow(flowstep, targetFlowId);
    this.flowstep = updatedFlowstep;
    this.initialFlowstep = updatedFlowstep;
  }

  async deleteFlowstep() {
    const flowstep = this.flowstep;
    if (!flowstep) {
      return;
    }
    if (flowstep.flowId) {
      dataStore.deleteFlowstepFromFlow(flowstep, flowstep.flowId);
    }
    await flowstepStorage.deleteFlowstep(flowstep);
    return true;
  }

  get validationErrors(): ValidationErrorsMap<BaseValidationErrorKey> {
    return new Map();
  }

  get isNotSaved() {
    if (!this.flowstep?.id) {
      return false;
    }
    const flowstepStr = JSON.stringify(this.buildFlowstep());
    const initialStr = JSON.stringify(this.initialFlowstep);

    return flowstepStr !== initialStr && this.validationErrors.size === 0;
  }

  get isNewFlowstep(): boolean {
    return !this.flowstep?.id;
  }
}
